import React, { useState } from 'react';
import { Plus, X } from 'lucide-react';
import FieldInfoTip from '../ui/FieldInfoTip.jsx';
import { getEquipementPresets } from '../../utils/prestationEquipementDetails.js';

const ItemChip = ({ label, onRemove }) => (
  <span className="inline-flex items-center gap-1 rounded-full bg-gray-100 px-2.5 py-1 text-xs text-gray-800">
    {label}
    <button
      type="button"
      onClick={onRemove}
      className="rounded-full p-0.5 text-gray-500 hover:bg-gray-200 hover:text-gray-800"
      aria-label={`Retirer ${label}`}
    >
      <X className="h-3 w-3" />
    </button>
  </span>
);

const QuickAdd = ({ presets, existing, onAdd, placeholder }) => {
  const [draft, setDraft] = useState('');
  const available = presets.filter((p) => !existing.includes(p));

  const submit = (value) => {
    const trimmed = String(value || '').trim();
    if (!trimmed || existing.includes(trimmed)) return;
    onAdd(trimmed);
    setDraft('');
  };

  return (
    <div className="space-y-2">
      {available.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          {available.slice(0, 6).map((preset) => (
            <button
              key={preset}
              type="button"
              onClick={() => submit(preset)}
              className="rounded-full border border-dashed border-gray-300 px-2 py-0.5 text-xs text-gray-600 hover:border-indigo-400 hover:text-indigo-700"
            >
              + {preset}
            </button>
          ))}
        </div>
      )}
      <div className="flex gap-2">
        <input
          type="text"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              submit(draft);
            }
          }}
          placeholder={placeholder}
          className="flex-1 rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
        />
        <button
          type="button"
          onClick={() => submit(draft)}
          className="inline-flex items-center rounded-lg border border-gray-200 px-2.5 text-sm text-gray-700 hover:bg-gray-50"
        >
          <Plus className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};

export default function PrestationEquipementSection({
  categorie,
  equipementFourni,
  onEquipementFourniChange,
  equipementRequis,
  onEquipementRequisChange,
  equipementFourniGratuit,
  onEquipementFourniGratuitChange,
  equipementFourniPayant,
  onEquipementFourniPayantChange,
  preparationInstructions,
  onPreparationInstructionsChange,
}) {
  const presets = getEquipementPresets(categorie);
  const [payantDraft, setPayantDraft] = useState({ nom: '', prix: '' });

  const addPayant = () => {
    const nom = payantDraft.nom.trim();
    const prix = parseFloat(payantDraft.prix);
    if (!nom || Number.isNaN(prix) || prix < 0) return;
    onEquipementFourniPayantChange([...equipementFourniPayant, { nom, prix }]);
    setPayantDraft({ nom: '', prix: '' });
  };

  return (
    <div className="space-y-5">
      <div className="grid gap-4 md:grid-cols-2">
        <div className="rounded-lg border border-amber-200 bg-amber-50/50 p-4">
          <div className="mb-2 flex items-center gap-1.5">
            <h4 className="text-sm font-semibold text-gray-900">Équipement requis</h4>
            <FieldInfoTip label="Équipement requis">
              Ce que le client doit apporter ou prévoir lui-même (obligatoire pour participer).
              Ex. bottes, certificat médical, tenue adaptée.
            </FieldInfoTip>
          </div>
          <p className="mb-3 text-xs text-gray-600">
            Indiquez tout ce que le client doit avoir sur lui, même si vous fournissez aussi du matériel.
          </p>
          {equipementRequis.length > 0 && (
            <div className="mb-3 flex flex-wrap gap-1.5">
              {equipementRequis.map((item) => (
                <ItemChip
                  key={item}
                  label={item}
                  onRemove={() => onEquipementRequisChange(equipementRequis.filter((v) => v !== item))}
                />
              ))}
            </div>
          )}
          <QuickAdd
            presets={presets.requis}
            existing={equipementRequis}
            onAdd={(item) => onEquipementRequisChange([...equipementRequis, item])}
            placeholder="Ex. Bottes, certificat médical…"
          />
        </div>

        <div className="rounded-lg border border-emerald-200 bg-emerald-50/50 p-4">
          <label className="mb-3 flex items-start gap-3">
            <input
              type="checkbox"
              checked={equipementFourni}
              onChange={(e) => onEquipementFourniChange(e.target.checked)}
              className="mt-1 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <div>
              <div className="flex items-center gap-1.5">
                <span className="text-sm font-semibold text-gray-900">Équipement fourni</span>
                <FieldInfoTip label="Équipement fourni">
                  Matériel mis à disposition par votre établissement. Précisez ce qui est inclus gratuitement
                  dans le prix et ce qui est proposé en location payante.
                </FieldInfoTip>
              </div>
              <p className="text-xs text-gray-600">
                Cochez si vous prêtez ou louez du matériel. Détaillez ensuite gratuit vs payant.
              </p>
            </div>
          </label>

          {equipementFourni && (
            <div className="space-y-4 border-t border-emerald-200 pt-4">
              <div>
                <p className="mb-2 text-xs font-medium text-emerald-900">Inclus gratuitement dans le prix</p>
                {equipementFourniGratuit.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {equipementFourniGratuit.map((item) => (
                      <ItemChip
                        key={item}
                        label={item}
                        onRemove={() =>
                          onEquipementFourniGratuitChange(
                            equipementFourniGratuit.filter((v) => v !== item),
                          )
                        }
                      />
                    ))}
                  </div>
                )}
                <QuickAdd
                  presets={presets.fourniGratuit}
                  existing={equipementFourniGratuit}
                  onAdd={(item) => onEquipementFourniGratuitChange([...equipementFourniGratuit, item])}
                  placeholder="Ex. Casque, selle…"
                />
              </div>

              <div>
                <p className="mb-2 text-xs font-medium text-emerald-900">Location payante (en supplément)</p>
                {equipementFourniPayant.length > 0 && (
                  <ul className="mb-2 space-y-1">
                    {equipementFourniPayant.map((row, idx) => (
                      <li
                        key={`${row.nom}-${idx}`}
                        className="flex items-center justify-between rounded-md bg-white px-2 py-1 text-xs text-gray-800"
                      >
                        <span>
                          {row.nom} — {Number(row.prix).toFixed(2)} €
                        </span>
                        <button
                          type="button"
                          onClick={() =>
                            onEquipementFourniPayantChange(
                              equipementFourniPayant.filter((_, i) => i !== idx),
                            )
                          }
                          className="text-red-500 hover:text-red-700"
                          aria-label={`Retirer ${row.nom}`}
                        >
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
                <div className="flex flex-wrap gap-2">
                  <input
                    type="text"
                    value={payantDraft.nom}
                    onChange={(e) => setPayantDraft((d) => ({ ...d, nom: e.target.value }))}
                    placeholder="Ex. Bottes location"
                    className="min-w-[8rem] flex-1 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                  />
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={payantDraft.prix}
                    onChange={(e) => setPayantDraft((d) => ({ ...d, prix: e.target.value }))}
                    placeholder="Prix €"
                    className="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-sm"
                  />
                  <button
                    type="button"
                    onClick={addPayant}
                    className="rounded-lg border border-gray-200 px-2.5 text-sm text-gray-700 hover:bg-white"
                  >
                    Ajouter
                  </button>
                </div>
                <p className="mt-2 text-xs text-gray-500">
                  Pour une option sélectionnable au checkout, vous pourrez aussi créer une variante dans
                  « Variantes & options » après enregistrement.
                </p>
              </div>
            </div>
          )}
        </div>
      </div>

      <div>
        <label className="text-sm font-medium text-gray-700 mb-1 flex items-center gap-1.5">
          Instructions de préparation
          <FieldInfoTip label="Instructions de préparation">
            Conseils généraux affichés au client : arrivée en avance, tenue, annulation, météo, etc.
          </FieldInfoTip>
        </label>
        <textarea
          value={preparationInstructions}
          onChange={(e) => onPreparationInstructionsChange(e.target.value)}
          className="w-full rounded-lg border border-gray-200 px-3 py-2 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-200"
          rows="3"
          placeholder="Ex. Arriver 15 min avant, certificat médical à présenter…"
        />
      </div>
    </div>
  );
}
